import React, { useMemo } from "react";
import styled from "styled-components";
import MetricTrend from "@charty/metric-trend";
import { content } from "@glossy";
import { Metric } from "@linkfluence/models/metric";
import {
  Platform,
  isOfflineMediaPlatform,
  isWebPlatform,
} from "@linkfluence/models/platform";
import { evolution } from "@tootools/number";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import {
  CashType,
  countriesEMV,
  defaultEMV,
} from "@radarly/models/cards/types/emv";
import { SearchFragmentEmv } from "@radarly/models/search";

interface SharedWidgetsEmvInnerProps {
  comparisonData: SearchFragmentEmv["data"];
  data: SearchFragmentEmv["data"];
  onClick?: () => void;
  disableAnimation?: boolean;
  showEvolution?: boolean;
}

function getCashType(platform: Platform): CashType {
  if (isOfflineMediaPlatform(platform)) {
    return CashType.Print;
  }
  if (isWebPlatform(platform)) {
    return CashType.Web;
  }
  return CashType.Social;
}

function getEmvTotal(data: SearchFragmentEmv["data"]): number {
  if (data?.aggs?.date) {
    return data.aggs.date.values.reduce((total, date) => {
      const _dateTotal = date.aggs.platforms.values.reduce(
        (dateTotal, platform) => {
          const cashType = getCashType(platform.key);
          const metric =
            cashType === CashType.Web ? Metric.Impression : Metric.Reach;
          const _platformTotal = platform.aggs.countries.values.reduce(
            (platformsTotal, country) => {
              const _country =
                countriesEMV[country.key.toUpperCase()] || defaultEMV;
              const cash =
                ((country.counts[metric] || 0) / 1000) *
                (_country[cashType] || 0);
              return (platformsTotal += cash);
            },
            0
          );
          return (dateTotal += _platformTotal);
        },
        0
      );
      return total + _dateTotal;
    }, 0);
  }
  return 0;
}

const Root = styled.div`
  display: flex;
  align-items: end;
  gap: 6px;
`;

const Currency = styled.div`
  color: ${content};
`;

export default function SharedWidgetsEmvChart({
  data,
  comparisonData,
  onClick,
  disableAnimation,
  showEvolution,
}: SharedWidgetsEmvInnerProps) {
  const locale = useCurrentUserLocale();
  const emvTotal = useMemo(() => getEmvTotal(data), [data]);
  const emvComparisonTotal = useMemo(
    () => getEmvTotal(comparisonData),
    [comparisonData]
  );
  return (
    <Root>
      <Currency>EUR</Currency>
      <MetricTrend
        count={emvTotal}
        locale={locale}
        evolution={
          showEvolution ? evolution(emvComparisonTotal, emvTotal) : null
        }
        data={null}
        onClick={onClick}
        isPercentage={false}
        disableAnimation={disableAnimation}
      />
    </Root>
  );
}
